import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import {
  Box,
  Divider,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
} from '@mui/material';
import {
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
} from '@mui/icons-material';
import AiSparkleIcon from '@diligentcorp/atlas-react-icons/dist/esm/lens/AiSparkle.js';
import DashboardIcon from '@diligentcorp/atlas-react-icons/dist/esm/lens/Dashboard.js';
import AcademyIcon from '@diligentcorp/atlas-react-icons/dist/esm/lens/Academy.js';
import CommunityAppIcon from '@diligentcorp/atlas-react-icons/dist/esm/lens/CommunityApp.js';
import FrameworksIcon from '@diligentcorp/atlas-react-icons/dist/esm/lens/Frameworks.js';
import OrganizationIcon from '@diligentcorp/atlas-react-icons/dist/esm/lens/Organization.js';
import SettingsIcon from '@diligentcorp/atlas-react-icons/dist/esm/lens/Settings.js';
import BookIcon from '@diligentcorp/atlas-react-icons/dist/esm/lens/Book.js';
import { ContentAuthoringNavIcon } from '../GlobalNav/contentAuthoringNavIcons';
import { useUserProfile } from '../../contexts/UserProfileContext';
import AppConfig from '../../AppConfig';
import {
  contentAuthoringPanelOverrideOnClose,
  isContentAuthoringNavPath,
  isContentAuthoringPanelOpen,
} from '../GlobalNav/complianceEducationNavPaths';

/** Matches Atlas global navigator panel width used in deployed environments. */
const SIDE_NAV_WIDTH_PX = 260;
/** Atlas --lens-component-global-nav-collapsed-width (lens-core-spacing-8). */
const SIDE_NAV_COLLAPSED_WIDTH_PX = 64;

const MONOCHROME_NAV_ICON_FILTER = 'brightness(0)';
const ICON_SIZE = 20;

function MonochromeAiSparkleNavIcon({ style }: { style?: React.CSSProperties }) {
  return (
    <AiSparkleIcon
      style={{
        ...style,
        filter: MONOCHROME_NAV_ICON_FILTER,
        color: 'currentColor',
      }}
    />
  );
}

function isPathActive(pathname: string, to: string): boolean {
  return pathname === to || pathname.startsWith(`${to}/`);
}

interface LocalDevNavItemProps {
  to: string;
  label: string;
  icon: React.ComponentType<{ style?: React.CSSProperties }>;
  endIcon?: React.ReactNode;
  indent?: boolean;
  collapsed?: boolean;
}

interface LocalDevActionNavItemProps {
  label: string;
  icon: React.ComponentType<{ style?: React.CSSProperties }>;
  endIcon?: React.ReactNode;
  collapsed?: boolean;
  selected?: boolean;
  onClick: () => void;
}

function LocalDevActionNavItem({
  label,
  icon: Icon,
  endIcon,
  collapsed = false,
  selected = false,
  onClick,
}: LocalDevActionNavItemProps) {
  return (
    <ListItemButton
      onClick={onClick}
      selected={selected}
      aria-label={collapsed ? label : undefined}
      title={collapsed ? label : undefined}
      sx={{
        textTransform: 'none',
        py: collapsed ? 1.25 : 1,
        pl: collapsed ? 1 : 2,
        pr: collapsed ? 1 : 1.5,
        justifyContent: collapsed ? 'center' : 'flex-start',
        mx: collapsed ? 0.5 : 0,
        borderRadius: collapsed ? 1 : 0,
        '&.Mui-selected': collapsed
          ? {
              bgcolor: 'action.selected',
              borderLeft: 3,
              borderColor: 'primary.main',
            }
          : undefined,
      }}
    >
      <ListItemIcon
        sx={{
          minWidth: collapsed ? 0 : 36,
          justifyContent: 'center',
        }}
      >
        <Icon style={{ width: ICON_SIZE, height: ICON_SIZE }} />
      </ListItemIcon>
      {!collapsed ? (
        <>
          <ListItemText
            primary={label}
            primaryTypographyProps={{ variant: 'body1', noWrap: true }}
          />
          {endIcon}
        </>
      ) : null}
    </ListItemButton>
  );
}

function LocalDevNavItem({
  to,
  label,
  icon: Icon,
  endIcon,
  indent = false,
  collapsed = false,
}: LocalDevNavItemProps) {
  const { pathname } = useLocation();

  return (
    <ListItemButton
      component={RouterLink}
      to={to}
      selected={isPathActive(pathname, to)}
      aria-label={collapsed ? label : undefined}
      title={collapsed ? label : undefined}
      sx={{
        textTransform: 'none',
        py: collapsed ? 1.25 : 1,
        pl: collapsed ? 1 : indent ? 3 : 2,
        pr: collapsed ? 1 : 1.5,
        justifyContent: collapsed ? 'center' : 'flex-start',
        mx: collapsed ? 0.5 : 0,
        borderRadius: collapsed ? 1 : 0,
        '&.Mui-selected': collapsed
          ? {
              bgcolor: 'action.selected',
              borderLeft: 3,
              borderColor: 'primary.main',
            }
          : undefined,
      }}
    >
      <ListItemIcon
        sx={{
          minWidth: collapsed ? 0 : 36,
          justifyContent: 'center',
        }}
      >
        <Icon style={{ width: ICON_SIZE, height: ICON_SIZE }} />
      </ListItemIcon>
      {!collapsed ? (
        <>
          <ListItemText
            primary={label}
            primaryTypographyProps={{ variant: 'body1', noWrap: true }}
          />
          {endIcon}
        </>
      ) : null}
    </ListItemButton>
  );
}

interface LocalDevBackNavItemProps {
  label: string;
  collapsed?: boolean;
  onClick: () => void;
}

function LocalDevBackNavItem({ label, collapsed = false, onClick }: LocalDevBackNavItemProps) {
  return (
    <ListItemButton
      onClick={onClick}
      aria-label={collapsed ? label : undefined}
      title={collapsed ? label : undefined}
      sx={{
        textTransform: 'none',
        py: collapsed ? 1.25 : 1.25,
        pl: collapsed ? 1 : 1.5,
        pr: collapsed ? 1 : 1.5,
        justifyContent: collapsed ? 'center' : 'flex-start',
        color: 'text.secondary',
        mx: collapsed ? 0.5 : 0,
      }}
    >
      <ListItemIcon
        sx={{
          minWidth: collapsed ? 0 : 28,
          justifyContent: 'center',
        }}
      >
        <ChevronLeftIcon fontSize="small" />
      </ListItemIcon>
      {!collapsed ? (
        <ListItemText
          primary={label}
          primaryTypographyProps={{ variant: 'body1', fontWeight: 500, noWrap: true }}
        />
      ) : null}
    </ListItemButton>
  );
}

function SectionLabel({ children }: { children: string }) {
  return (
    <Typography
      variant="overline"
      color="text.secondary"
      sx={{ display: 'block', px: 2, pt: 1.5, pb: 0.5, lineHeight: 1.4 }}
    >
      {children}
    </Typography>
  );
}

export interface LocalDevSideNavProps {
  open: boolean;
}

/**
 * Local-only side navigation panel. Mirrors GlobalNavigator panel behavior
 * (flat and hierarchical Compliance Education nav) without loading hb-global-navigator.
 * Only rendered when AppConfig.isLocal ? no effect on dev, staging, or production.
 */
export function LocalDevSideNav({ open }: LocalDevSideNavProps): React.ReactNode {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const [authoringPanelOverride, setAuthoringPanelOverride] = React.useState<boolean | null>(
    null,
  );
  const {
    canAccessDashboard,
    canAccessCourses,
    canAccessSettings,
    canAccessAccounts,
    canAccessUsers,
    canAccessContentManagement,
    canAccessTemplates,
    canAccessAiRecommendation,
  } = useUserProfile();

  const showSideNav =
    canAccessDashboard ||
    canAccessCourses ||
    (canAccessSettings && AppConfig.userSettingsEnabled) ||
    canAccessAccounts ||
    canAccessUsers ||
    canAccessContentManagement ||
    canAccessTemplates ||
    canAccessAiRecommendation;

  const isOnContentAuthoringRoute =
    AppConfig.complianceEducationNavTreeEnabled &&
    canAccessTemplates &&
    isContentAuthoringNavPath(pathname);

  const isInContentAuthoringPanel =
    AppConfig.complianceEducationNavTreeEnabled &&
    canAccessTemplates &&
    isContentAuthoringPanelOpen(pathname, authoringPanelOverride);

  React.useEffect(() => {
    if (!isOnContentAuthoringRoute) {
      setAuthoringPanelOverride(null);
    }
  }, [isOnContentAuthoringRoute]);

  const showComplianceEducationSection =
    canAccessContentManagement || canAccessTemplates;

  const collapsed = !open;
  const sideNavWidthPx = open ? SIDE_NAV_WIDTH_PX : SIDE_NAV_COLLAPSED_WIDTH_PX;

  React.useEffect(() => {
    document.documentElement.style.setProperty(
      '--atlas-global-nav-content-left',
      showSideNav ? `${sideNavWidthPx}px` : '0px',
    );

    return () => {
      document.documentElement.style.setProperty('--atlas-global-nav-content-left', '0px');
    };
  }, [showSideNav, sideNavWidthPx]);

  if (!showSideNav) {
    return null;
  }

  const expandChevron = (
    <ChevronRightIcon fontSize="small" sx={{ color: 'text.secondary', ml: 0.5 }} />
  );

  return (
    <Box
      id="local-dev-side-nav"
      component="nav"
      aria-label={t('nav.compliance_education.title')}
      sx={{
        width: sideNavWidthPx,
        flexShrink: 0,
        borderRight: 1,
        borderColor: 'divider',
        bgcolor: 'background.paper',
        overflowY: 'auto',
        overflowX: 'hidden',
        transition: (theme) =>
          theme.transitions.create('width', {
            easing: theme.transitions.easing.easeInOut,
            duration: theme.transitions.duration.shortest,
          }),
      }}
    >
      {!collapsed ? (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: 'block', px: 2, pt: 2, pb: 0.5 }}
        >
          Local dev navigation
        </Typography>
      ) : (
        <Box sx={{ pt: 1 }} />
      )}

      {isInContentAuthoringPanel ? (
        <>
          <LocalDevBackNavItem
            onClick={() => {
              setAuthoringPanelOverride(
                contentAuthoringPanelOverrideOnClose(isOnContentAuthoringRoute),
              );
            }}
            label={t('nav.compliance_education.back_to_compliance_education')}
            collapsed={collapsed}
          />
          {!collapsed ? <Divider sx={{ my: 0.5 }} /> : null}
          {!collapsed ? (
            <SectionLabel>{t('nav.compliance_education.content_authoring')}</SectionLabel>
          ) : null}
          <List dense disablePadding>
            <LocalDevNavItem
              to="/templates"
              label={t('nav.compliance_education.templates')}
              icon={BookIcon}
              collapsed={collapsed}
            />
            <LocalDevNavItem
              to="/authoring/courses"
              label={t('nav.compliance_education.authoring_courses')}
              icon={AcademyIcon}
              collapsed={collapsed}
            />
          </List>
        </>
      ) : (
        <List dense disablePadding>
          {canAccessDashboard ? (
            <LocalDevNavItem
              to="/review-center"
              label="Review Center"
              icon={DashboardIcon}
              collapsed={collapsed}
            />
          ) : null}
          {canAccessCourses ? (
            <LocalDevNavItem
              to="/courses"
              label={t('nav.courses')}
              icon={AcademyIcon}
              collapsed={collapsed}
            />
          ) : null}
          {canAccessSettings && AppConfig.userSettingsEnabled ? (
            <LocalDevNavItem
              to="/settings"
              label={t('nav.settings')}
              icon={SettingsIcon}
              collapsed={collapsed}
            />
          ) : null}
          {canAccessAccounts ? (
            <LocalDevNavItem
              to="/accounts"
              label="Accounts"
              icon={OrganizationIcon}
              collapsed={collapsed}
            />
          ) : null}
          {canAccessUsers ? (
            <LocalDevNavItem
              to="/operators"
              label="Operators"
              icon={CommunityAppIcon}
              collapsed={collapsed}
            />
          ) : null}

          {showComplianceEducationSection ? (
            <>
              {!collapsed ? <Divider sx={{ my: 1 }} /> : null}
              {!collapsed ? (
                <SectionLabel>{t('nav.compliance_education.title')}</SectionLabel>
              ) : null}
              {AppConfig.complianceEducationNavTreeEnabled ? (
                <>
                  {canAccessContentManagement ? (
                    <LocalDevNavItem
                      to="/manage-content"
                      label={t('nav.compliance_education.customized_content')}
                      icon={AcademyIcon}
                      collapsed={collapsed}
                    />
                  ) : null}
                  {canAccessTemplates ? (
                    <LocalDevActionNavItem
                      label={t('nav.compliance_education.content_authoring')}
                      icon={ContentAuthoringNavIcon}
                      endIcon={expandChevron}
                      collapsed={collapsed}
                      selected={isInContentAuthoringPanel}
                      onClick={() => setAuthoringPanelOverride(true)}
                    />
                  ) : null}
                </>
              ) : (
                <>
                  {canAccessTemplates ? (
                    <LocalDevNavItem
                      to="/templates"
                      label={t('nav.compliance_education.templates')}
                      icon={FrameworksIcon}
                      collapsed={collapsed}
                    />
                  ) : null}
                  {canAccessContentManagement ? (
                    <LocalDevNavItem
                      to="/manage-content"
                      label={t('nav.compliance_education.customized_content')}
                      icon={AcademyIcon}
                      collapsed={collapsed}
                    />
                  ) : null}
                </>
              )}
            </>
          ) : null}

          {canAccessAiRecommendation ? (
            <>
              {!collapsed ? <Divider sx={{ my: 1 }} /> : null}
              <LocalDevNavItem
                to="/ai-recommendation"
                label={t('nav.ai_recommendation')}
                icon={MonochromeAiSparkleNavIcon}
                collapsed={collapsed}
              />
            </>
          ) : null}
        </List>
      )}
    </Box>
  );
}
